// Payment-return handler — ports onboarding.js _checkPaymentSuccess.
// Tochka redirects back to <app>#/payment/success?order_id=... in the same tab.
// We poll /payments/status/{id} (webhook may lag) and react: service purchase →
// open psychologist chat; subscription → refresh stats + toast.

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { paymentsApi } from '../api/payments'
import { chatApi } from '../api/chat'
import { chatwootApi } from '../api/chatwoot'
import { useProfileStore } from '../store/profileStore'
import { useChatStore } from '../store/chatStore'
import { useMessagingStore } from '../store/messagingStore'
import { useInstallGuideStore } from '../store/installGuideStore'
import { toast } from '../store/toastStore'
import * as metrica from '../lib/metrica'

const MAX_ATTEMPTS = 12
const POLL_MS = 10000

interface PaymentStatus {
  status: string
  is_service?: boolean
  plan_id?: string
}

export function usePaymentSuccess() {
  const navigate = useNavigate()
  const loadStats = useProfileStore((s) => s.loadStats)

  useEffect(() => {
    const hash = window.location.hash
    if (!hash.startsWith('#/payment/success')) return

    const params = new URLSearchParams(hash.split('?')[1] || '')
    const orderId = params.get('order_id')
    // Clean the hash so a refresh doesn't re-trigger.
    window.history.replaceState(null, '', window.location.pathname + window.location.search)
    if (!orderId) return

    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const poll = async () => {
      attempts += 1
      if (cancelled || attempts > MAX_ATTEMPTS) return
      try {
        const data = (await paymentsApi.status(orderId)) as PaymentStatus
        if (data.status === 'success') {
          useMessagingStore.getState().clearPaywall()
          if (data.is_service) {
            metrica.reachGoal('service_purchased', { plan: data.plan_id })
            toast('Сессия оплачена! Открываем чат с психологом...', 'success')
            chatwootApi.sendMessage({ order_id: orderId }).catch(() => {})
            // Tell the Psychologist screen to pop the Chatwoot widget on mount
            // (original opened it directly, onboarding.js:189). Skip the
            // install-guide popup here — it would stack on top of the widget
            // it's about to auto-open; it still gets shown after a
            // subscription purchase below, and after registration.
            useChatStore.getState().requestChatwootOpen()
            navigate('/psychologist')
          } else {
            metrica.reachGoal('subscription_activated', { plan: data.plan_id })
            metrica.reachGoal(`subscription_activated_${data.plan_id}`)
            toast('Подписка активирована!', 'success')
            // §0.6: transfer the user to engine v2 now; the message path
            // retriggers lazily if this call is lost (closed tab, offline)
            chatApi.engineHandover(data.plan_id).catch(() => {})
            loadStats(true)
            useInstallGuideStore.getState().requestShow()
          }
          return
        }
      } catch {
        /* keep polling */
      }
      timer = setTimeout(poll, POLL_MS)
    }
    poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [navigate, loadStats])
}
